// Dark Web Exposure Scan fixtures for demo mode.
//
// The footprint is computed live and never stored, so the demo mirrors that exactly: a pass
// starts when the reader presses Start, the first read reports it running, the second hands back
// what it found, and a deeper pass goes out with the fragments the reader disowned excluded.
// Nothing here survives a reload, which is what the real product does too.
//
// Everything is in WIRE shape — the envelopes the /footprint endpoints return — because the
// store maps them itself and a pre-mapped view model would exercise a path the product does not
// have. The shapes are the ones in types/person/footprint.ts.
//
// All values are fictional sample data about the demo persona, not real breach data.

import type { DemoRoute } from '../types';
import { DEMO_PERSONA } from '../persona';
import type {
    AddToVaultSummary,
    DatasetMeta,
    FootprintAccessInfo,
    FootprintRecordsResponse,
    FootprintShowResponse,
    FootprintSession,
    RawLeakedRecord,
    RawSnapshot,
    RawSnapshotSensitive,
    RawSnapshotSource,
    RawSnapshotValue,
    SensitiveRevealResponse,
} from '~/types/person/footprint';

/** The demo member holds Signature, so every level of the ladder is reachable. */
const ACCESS: FootprintAccessInfo = {
    tier: 'signature',
    can_scan: true,
    can_deepen: true,
    credentials_in_full: false,
    threat_hunter_available: false,
    max_depth: 3,
};

const PASSWORD_KEY = 'd3m0d3m0d3m0d3m0d3m0d3m0d3m0d3m0';
const HASHED_PASSWORD_KEY = 'ba5e64ba5e64ba5e64ba5e64ba5e64ba';
const SSN_KEY = '5c5a5f5e5d5c5b5a59585756555453d0';

/**
 * The value behind each masked row as the source holds it, keyed the way the reveal endpoint
 * keys one. The hashed password's is the hash itself, which is what a reveal of one returns.
 */
const CLEARTEXT: Record<string, string> = {
    [PASSWORD_KEY]: 'Scr4nton!Paper',
    [HASHED_PASSWORD_KEY]: 'kV3x9QmP2sLr7TzN4bWc8YdF1gE=',
    [SSN_KEY]: '198-44-0271',
};

const value = (field_type: string, normalized_value: string, display_value: string, record_count: number, source_count: number): RawSnapshotValue => ({
    field_type,
    normalized_value,
    display_value,
    record_count,
    source_count,
    in_vault: false,
});

/**
 * What each wave turns up, cumulatively: level 1 is what a surface sweep sees, and each level
 * below it adds what the one above could not reach.
 */
const WAVES: RawSnapshotValue[][] = [
    [
        value('email', DEMO_PERSONA.email, DEMO_PERSONA.email, 24, 9),
        value('first_name', 'michael', 'Michael', 18, 8),
        value('last_name', 'scott', 'Scott', 18, 8),
        value('phone', '15705550142', '(570) 555-0142', 6, 3),
    ],
    [
        value('email', 'mscott@dundermifflin.example', 'mscott@dundermifflin.example', 7, 4),
        value('username', 'worldsbestboss', 'worldsbestboss', 5, 3),
        value('street_address', '1725 slough avenue', '1725 Slough Avenue', 4, 2),
    ],
    [
        value('city', 'scranton', 'Scranton', 9, 5),
        value('zip', '18505', '18505', 9, 5),
        value('email', 'mikescarn@example.net', 'mikescarn@example.net', 2, 1),
    ],
];

/**
 * One password of each class beside the SSN, so the demo walks the gate in both its words: the
 * plaintext password in full, and the hashed one as the hash it was found as. The masks are the
 * server's shape, first and last character around a fixed run of bullets.
 */
const SENSITIVE: RawSnapshotSensitive[] = [
    {
        field_type: 'password',
        key: PASSWORD_KEY,
        display_value: 'S•••r',
        record_count: 11,
        source_count: 5,
        password_class: { kind: 'raw', hash_type: null },
    },
    {
        field_type: 'password',
        key: HASHED_PASSWORD_KEY,
        display_value: 'k•••=',
        record_count: 4,
        source_count: 2,
        password_class: { kind: 'hashed', hash_type: 'base64-digest' },
    },
    { field_type: 'ssn', key: SSN_KEY, display_value: '•••-••-0271', record_count: 3, source_count: 2, password_class: null },
];

/**
 * What the vendor's dataset catalogue knows about each source, keyed the way a record names its
 * source. RiverCity Forum is deliberately absent: the catalogue is public and partial, plenty of
 * datasets are in it under no category at all, and a demo where every source is fully described
 * teaches a page that does not exist.
 */
const DATASETS: Record<string, DatasetMeta> = {
    Apollo: {
        dataset_id: 'apollo',
        category: 'scraping',
        source_domain: 'apollo.io',
        incident_date: '2018-07-01',
        disclosed_at: '2018-10-05',
        description: 'A sales contact database assembled from public profiles and left on an unsecured server.',
        total_records: 125929660,
        tags: ['business', 'contact', 'profiles'],
    },
    Dropbox: {
        dataset_id: 'dropbox',
        category: 'database',
        source_domain: 'dropbox.com',
        incident_date: '2012-07-01',
        disclosed_at: '2016-08-31',
        description: 'Account credentials taken from the file sharing service and traded four years before they surfaced.',
        total_records: 68648009,
        tags: ['credentials', 'accounts'],
    },
};

const SOURCES: RawSnapshotSource[] = [
    { source_key: 'apollo', display_name: 'Apollo', record_count: 12, verified: false },
    { source_key: 'dropbox', display_name: 'Dropbox', record_count: 9, verified: true },
    { source_key: 'riverforum', display_name: 'RiverCity Forum', record_count: 6, verified: false },
].map((source) => {
    // The catalogue keys are read at serialization time from the same table the drill reads, so
    // the two surfaces can never describe one dataset differently. A source it does not know
    // keeps every one of them null.
    const described = DATASETS[source.display_name] ?? null;

    return {
        ...source,
        category: described?.category ?? null,
        source_domain: described?.source_domain ?? null,
        incident_date: described?.incident_date ?? null,
        disclosed_at: described?.disclosed_at ?? null,
        total_records: described?.total_records ?? null,
    };
});

/**
 * The state one demo session carries. Two depths, not one: the pass that was ASKED for and the
 * deepest pass whose results are available. A read taken while a pass runs answers with what the
 * previous pass found, which is what the real endpoint does — answering with nothing would blank
 * the level the reader is standing on every time they descend.
 */
let askedDepth = 0;
let settledDepth = 0;
let running = false;
let pendingLease = false;
let lease: FootprintSession | null = null;
const SESSION_MS = 30 * 60 * 1000;

function currentSession(): FootprintSession | null {
    if (!lease) return null;
    if (Date.parse(lease.expires_at) <= Date.now()) {
        lease = null;
        settledDepth = 0;
        return null;
    }
    return { ...lease, server_now: new Date().toISOString() };
}
/** Normalized values the reader marked as not theirs; the next wave goes out without them. */
const excluded = new Set<string>();

/** Milliseconds a demo pass reports: a deeper wave reads more sources, so it costs more wall clock. */
function passDurationMs(depth: number): number {
    return 442 + 1400 * depth;
}

function snapshotAt(depth: number): RawSnapshot {
    return {
        depth,
        scanned_at: new Date().toISOString(),
        duration_ms: passDurationMs(depth),
        values: WAVES.slice(0, depth)
            .flat()
            .filter((row) => !excluded.has(row.normalized_value)),
        sources: SOURCES.slice(0, Math.max(1, depth)),
        // Credentials surface from the second wave down: a surface sweep does not reach them.
        sensitive: depth >= 2 ? SENSITIVE : [],
    };
}

function envelope(raw: RawSnapshot | null, status: FootprintShowResponse['status'], depth: number): FootprintShowResponse {
    return {
        data: raw,
        session: currentSession(),
        status,
        reason: null,
        retry_after: null,
        access: ACCESS,
        no_usable_seeds: false,
        values_truncated: false,
        sources_truncated: false,
        sensitive_truncated: false,
        // The distinct record total, which is smaller than the sum of the per-value counts
        // because one leaked row carries several of these values at once.
        totals: raw ? { sources: raw.sources.length, records: 31 + depth * 4 } : { sources: 0, records: 0 },
    };
}

function showResponse(): FootprintShowResponse {
    currentSession();
    // One read reports the pass running, the next hands back what it found, so the page's
    // scanning state is something a reader actually sees rather than a branch nothing enters.
    if (running) {
        running = false;
        settledDepth = askedDepth;
        pendingLease = true;

        // A wave in flight withholds the body of the wave it supersedes, totals and all: the
        // page reads the state from `status` and keeps the level the reader is standing on,
        // and the withheld body comes back when the pass ends.
        return envelope(null, 'scanning', settledDepth);
    }

    if (settledDepth === 0) return envelope(null, 'none', 0);

    if (pendingLease) {
        pendingLease = false;
        lease = {
            snapshot_id: crypto.randomUUID(),
            expires_at: new Date(Date.now() + SESSION_MS).toISOString(),
            server_now: new Date().toISOString(),
        };
    }
    return envelope(snapshotAt(settledDepth), 'ready', settledDepth);
}

/** Every value any wave carries, so a record field and the snapshot row it belongs to agree. */
const KNOWN_VALUES = WAVES.flat();

/**
 * The normalized spelling of one value. This side of the wire owns the rule — the browser never
 * derives it — so a value the waves already carry takes ITS normalized form rather than a second
 * answer computed here: a phone canonicalises through libphonenumber in the real backend, and a
 * demo that recomputed the digits would give the same number two identities and split one
 * fragment in two.
 */
function normalizedFor(fieldType: string, displayValue: string): { normalized_value: string; in_vault: boolean } {
    const known = KNOWN_VALUES.find((row) => row.field_type === fieldType && row.display_value === displayValue);

    if (known) return { normalized_value: known.normalized_value, in_vault: known.in_vault };

    return {
        normalized_value:
            fieldType === 'phone' || fieldType === 'ssn'
                ? displayValue.replace(/\D+/g, '')
                : displayValue.trim().replace(/\s+/g, ' ').toLowerCase(),
        in_vault: false,
    };
}

const record = (
    record_id: string,
    source: string,
    dataset: string,
    verified: boolean,
    fields: Array<[string, string]>,
    passwordClass: RawLeakedRecord['password_class'] = null
): RawLeakedRecord => ({
    source,
    dataset,
    record_id,
    line: null,
    verified,
    password_class: passwordClass,
    password_key: passwordClass?.kind === 'raw' && settledDepth >= 2 ? PASSWORD_KEY : null,
    fields: fields.map(([field_type, fieldValue]) => ({ field_type, value: fieldValue, ...normalizedFor(field_type, fieldValue) })),
});

function recordsFor(fieldType: string, displayValue: string): FootprintRecordsResponse {
    const records = [
        record('apollo-2018', 'Apollo', 'apollo.io · Jul 2018', false, [
            [fieldType, displayValue],
            ['first_name', DEMO_PERSONA.first_name],
            ['last_name', DEMO_PERSONA.last_name],
        ]),
        // The credential stays behind its reveal gate; records carry only its storage class.
        record(
            'dropbox-2012',
            'Dropbox',
            'dropbox.com · Jul 2012',
            true,
            [
                [fieldType, displayValue],
                ['username', 'worldsbestboss'],
            ],
            { kind: 'hashed', hash_type: 'bcrypt' }
        ),
        record('rivercity-2020', 'RiverCity Forum', 'rivercity.example · Mar 2020', false, [
            [fieldType, displayValue],
            ['city', DEMO_PERSONA.address.city],
        ], { kind: 'raw', hash_type: null }),
    ];

    return {
        data: {
            records,
            // One entry per DISTINCT source, present even where the catalogue knows nothing: an
            // absent key means the source is not in this page, which is a different thing.
            datasets: Object.fromEntries(records.map((row) => [row.source, DATASETS[row.source] ?? null])),
            sensitive: { password: 2 },
            next_cursor: null,
        },
    };
}

export const footprintRoutes: DemoRoute[] = [
    { pattern: /^people\/(\d+)\/footprint$/, respond: (): FootprintShowResponse => showResponse() },
    {
        pattern: /^people\/(\d+)\/footprint$/,
        method: 'PATCH',
        status: (req) => currentSession()?.snapshot_id === req.body?.snapshot_id ? 200 : 409,
        respond: (req) => {
            const current = currentSession();
            if (!current) return { error: 'scan_expired' };
            if (current.snapshot_id !== req.body?.snapshot_id) return { error: 'session_changed' };
            if (current.expires_at === req.body?.expected_expires_at) {
                lease = { ...current, expires_at: new Date(Date.now() + SESSION_MS).toISOString() };
            }
            return { session: currentSession() };
        },
    },
    {
        pattern: /^people\/(\d+)\/footprint\/scan$/,
        method: 'POST',
        status: 202,
        respond: (req) => {
            const body = (req.body ?? {}) as { depth?: number; negatives?: Array<{ value: string }> };

            for (const negative of body.negatives ?? []) excluded.add(negative.value);

            askedDepth = Math.min(Math.max(body.depth ?? 1, 1), WAVES.length);
            running = true;

            return { data: { status: 'scanning' as const, depth: askedDepth, reason: null, retry_after: null } };
        },
    },
    {
        pattern: /^people\/(\d+)\/footprint\/records$/,
        method: 'POST',
        respond: (req): FootprintRecordsResponse => {
            const body = (req.body ?? {}) as { field_type?: string; value?: string };

            return recordsFor(body.field_type ?? 'email', body.value ?? DEMO_PERSONA.email);
        },
    },
    {
        pattern: /^people\/(\d+)\/footprint\/add-to-vault$/,
        method: 'POST',
        respond: (req): { data: AddToVaultSummary } => {
            const body = (req.body ?? {}) as { values?: Array<{ field_type: string }> };
            const picked = body.values ?? [];

            // The hand-off stores phones, street addresses and names, and silently drops a
            // locality on its own — the demo has to report that honestly or it teaches the
            // reader a promise the product does not keep.
            return {
                data: {
                    phones: picked.filter((v) => v.field_type === 'phone').length,
                    addresses: picked.filter((v) => v.field_type === 'street_address').length,
                    names: picked.filter((v) => v.field_type === 'first_name' || v.field_type === 'last_name').length,
                },
            };
        },
    },
    {
        pattern: /^people\/(\d+)\/footprint\/sensitive-reveal$/,
        method: 'POST',
        respond: (req): SensitiveRevealResponse | null => {
            const body = (req.body ?? {}) as { key?: string };
            const cleartext = CLEARTEXT[body.key ?? ''];

            // Every handle the demo hands out is in the table above, so a miss means the page
            // asked for a handle nothing issued. The store reads a 200 carrying no value as a
            // plain failure, which is the right thing to say about that.
            return cleartext ? { data: { value: cleartext } } : null;
        },
    },
];
